import type {
  BoardElementId,
  ParticipantId,
  WallBoard,
  WallCard,
  WallShape,
} from '@walls/store';
import { getBoardCardElements } from '../../positioning/cardRects';
import { getCardPermissions } from '../hooks/useCardPermissions';
import { getShapePermissions } from '../../shapes/hooks/useShapePermissions';
import { isCardVisibleForParticipantFilter } from './cardVisibility';

export interface MovableSelectedCard {
  card: WallCard;
  startX: number;
  startY: number;
}

export interface MovableSelectedShape {
  shape: WallShape;
  startAttributes: Record<string, unknown>;
}

export interface MovableSelectedElements {
  cards: MovableSelectedCard[];
  shapes: MovableSelectedShape[];
}

export function getMovableSelectedCards({
  board,
  cards,
  currentParticipantId,
  pressedCard,
  selectedIds,
  visibleParticipantIds = [],
}: {
  board: WallBoard;
  cards: WallCard[];
  currentParticipantId: string | null;
  pressedCard: WallCard;
  selectedIds: BoardElementId[];
  visibleParticipantIds?: ParticipantId[];
}): MovableSelectedCard[] {
  return getMovableSelectedElements({
    board,
    cards,
    currentParticipantId,
    pressedElementId: pressedCard.id,
    selectedIds,
    shapes: [],
    visibleParticipantIds,
  }).cards;
}

export function getMovableSelectedElements({
  board,
  cards,
  currentParticipantId,
  pressedElementId,
  selectedIds,
  shapes,
  visibleParticipantIds = [],
}: {
  board: WallBoard;
  cards: WallCard[];
  currentParticipantId: string | null;
  pressedElementId: BoardElementId;
  selectedIds: BoardElementId[];
  shapes: WallShape[];
  visibleParticipantIds?: ParticipantId[];
}): MovableSelectedElements {
  const selectedCardIds = selectedIds.filter((selectedId) =>
    getBoardCardElements(cards).some((element) => element.id === selectedId)
  );
  const selectedShapeIds = selectedIds.filter((selectedId) =>
    shapes.some((shape) => shape.id === selectedId)
  );
  const pressedElementIsSelected =
    selectedCardIds.includes(pressedElementId) || selectedShapeIds.includes(pressedElementId);
  const draggedIds = pressedElementIsSelected
    ? [...selectedCardIds, ...selectedShapeIds]
    : [pressedElementId];
  const seenIds = new Set<BoardElementId>();
  const movableCards = getBoardCardElements(cards).flatMap((element) => {
    if (
      !element.card ||
      element.parentId ||
      seenIds.has(element.id) ||
      !draggedIds.includes(element.id) ||
      !isCardVisibleForParticipantFilter(element.card, visibleParticipantIds) ||
      !getCardPermissions(board, element.card, currentParticipantId).canMove ||
      element.card.xPos == null ||
      element.card.yPos == null
    ) {
      return [];
    }

    seenIds.add(element.id);

    return [
      {
        card: element.card,
        startX: element.card.xPos,
        startY: element.card.yPos,
      },
    ];
  });
  const movableShapes = shapes.flatMap((shape) => {
    if (
      shape.isLocked ||
      seenIds.has(shape.id) ||
      !draggedIds.includes(shape.id) ||
      !getShapePermissions(board, shape, currentParticipantId).canMove
    ) {
      return [];
    }

    seenIds.add(shape.id);

    return [
      {
        shape,
        startAttributes: { ...shape.attributes },
      },
    ];
  });

  return {
    cards: movableCards,
    shapes: movableShapes,
  };
}
